import { useEffect, useRef, useState } from 'react';
import { call } from './api';
import { uuid } from './plex';

/** Samen kijken: verbinding met een kamer op de server (Server-Sent Events). */

export interface RemoteState { playing: boolean; position: number; at: number; by: string; now: number }

const CLIENT_KEY = 'tumedia.togetherClient';
function clientId(): string {
  try {
    let id = sessionStorage.getItem(CLIENT_KEY);
    if (!id) {
      id = uuid();
      sessionStorage.setItem(CLIENT_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
}

export const togetherApi = {
  create: (ratingKey: string, title: string, position: number, playing: boolean) =>
    call<{ code: string }>('/api/together', { method: 'POST', body: JSON.stringify({ ratingKey, title, position, playing }) }),
  room: (code: string) => call<{ code: string; ratingKey: string; title: string; members: { id: string; name: string }[] }>(`/api/together/${encodeURIComponent(code)}`),
};

/**
 * Verbindt met kamer `code`. onState krijgt de stand van anderen, met de verwachte positie
 * (rekening gehouden met de tijd sinds die stand en het klokverschil met de server).
 */
export function useTogether(code: string | null, name: string, onState: (s: { playing: boolean; position: number }) => void) {
  const [members, setMembers] = useState<{ id: string; name: string }[]>([]);
  const [connected, setConnected] = useState(false);
  const cb = useRef(onState);
  cb.current = onState;
  const id = useRef(clientId());

  useEffect(() => {
    if (!code) return;
    const es = new EventSource(`/api/together/${encodeURIComponent(code)}/events?client=${encodeURIComponent(id.current)}&name=${encodeURIComponent(name)}`);
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.addEventListener('members', (e) => setMembers(JSON.parse((e as MessageEvent).data) as { id: string; name: string }[]));
    es.addEventListener('state', (e) => {
      const s = JSON.parse((e as MessageEvent).data) as RemoteState;
      if (s.by === id.current) return;
      const offset = s.now - Date.now(); // server min ons
      const elapsed = s.playing ? (Date.now() + offset - s.at) / 1000 : 0;
      cb.current({ playing: s.playing, position: s.position + Math.max(0, elapsed) });
    });
    return () => {
      es.close();
      setConnected(false);
      setMembers([]);
    };
  }, [code, name]);

  const send = (playing: boolean, position: number) => {
    if (!code) return;
    void call(`/api/together/${encodeURIComponent(code)}/state`, { method: 'POST', body: JSON.stringify({ client: id.current, playing, position }) }).catch(() => undefined);
  };

  return { members, connected, send, myId: id.current };
}
